"use client";

import { formatShortAddress } from "@cede/utils";
import { AddressText } from "@/components/shared/AddressText/AddressText";
import { UserAvatar } from "@/components/ui/avatar";
import { SkeletonElement } from "@/components/ui/skeleton";
import { useHubUser } from "@/hooks";
import { cn } from "@/lib/utils";

interface ProfileWalletDataProps {
  size?: number | string;
  vertical?: boolean;
}

const parseAvatarSize = (value?: number | string) => {
  if (typeof value === "number") {
    return value;
  }

  if (!value) {
    return 40;
  }

  const match = value.match(/\d+/);
  if (!match) {
    return 40;
  }

  const parsed = Number.parseInt(match[0] ?? "", 10);
  return Number.isNaN(parsed) ? 40 : parsed;
};

export const ProfileWalletData = ({ size = "57px", vertical = false }: ProfileWalletDataProps) => {
  const { hubUser, userFriendlyDefiAddress } = useHubUser();
  const avatarSize = parseAvatarSize(size);
  const sizeValue = typeof size === "number" ? `${size}px` : size;

  return (
    <div className={cn("flex w-full items-center gap-1 md:w-auto md:gap-4", vertical ? "flex-col" : "flex-row")}>
      <div className="flex-shrink-0 overflow-hidden rounded-full" style={{ width: sizeValue, height: sizeValue }}>
        <UserAvatar
          identifier={hubUser?.defiAddress ?? hubUser?.id}
          src="/avatar.png"
          alt="Profile avatar"
          size={avatarSize}
          className="h-full w-full"
          imageClassName="h-full w-full object-cover"
        />
      </div>
      <div className="flex items-center">
        {!hubUser?.defiAddress ? (
          <SkeletonElement className="h-7 w-36" />
        ) : (
          <AddressText asChild>
            <span className="text-lg font-semibold leading-[120%] tracking-[-0.005em] text-cede-text-primary md:text-2xl">
              {formatShortAddress(userFriendlyDefiAddress ?? "")}
            </span>
          </AddressText>
        )}
      </div>
    </div>
  );
};
